import { useState } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';

import CroeCropper from '@webclient/components/cropper';
import { notification } from '@webclient/components/notification';
import Footer from '@webclient/components/workspace/setup/components/footer';
import Header from '@webclient/components/workspace/setup/components/header';
import useWorkspaceSetup from '@webclient/components/workspace/setup/hooks/useWorkspaceSetup';
import ImagePreview from '@webclient/components/workspace/setup/sections/steps/components/image-preview';
import StyledImg from '@webclient/components/workspace/setup/sections/steps/cropper/styles';

import { storage } from '@webclient/lib/agent';

import useQueryParams from '@webclient/hooks/useQueryParams';

import 'cropperjs/dist/cropper.css';

import { useTranslate } from '@tolgee/react';
import AwsS3 from '@uppy/aws-s3';
import Uppy from '@uppy/core';

const AVATAR_SIZE = 48;

export default function Cropper() {
  const { t } = useTranslate();
  const [isUploadPending, setIsUploadPending] = useState(false);

  const {
    handleGoToPreviousStep,
    handleGoToStep,
    uploadedAvatar,
    selectedUploadOption,
    selectedIcon,
    handleReset,
  } = useWorkspaceSetup();
  const { removeQueryParamByKey } = useQueryParams();
  const [uppy] = useState(() =>
    new Uppy().use(AwsS3, {
      async getUploadParameters(file) {
        const name = file.data.name;
        const size = file.data.size;
        const sizeInKB = size / 1024;
        const type = file.type;

        const { data } = await storage.createPolicy<{
          usage: 'profile_picture';
        }>({
          size: sizeInKB,
          mimeType: type,
          name,
          context: {
            usage: 'profile_picture',
          },
        });
        return {
          method: 'PUT',
          fields: [],
          headers: {
            'Content-Type': type,
          },
          url: data.url,
        };
      },
    }),
  );

  uppy.on('upload-success', () => {
    notification.success({
      message: 'Your bot avatar was uploaded successfully',
      id: 'upload-success',
    });
  });

  uppy.on('upload-error', (error) => {
    console.error('Upload error:', error);
    notification.error({
      message: 'there was an error while uploading your bot avatar, try again',
      id: 'upload-error',
    });
  });

  function handleBackRedirectToPreviousStep() {
    if (selectedUploadOption == null) return;
    handleGoToPreviousStep();
  }

  function handleGetCroppedImage(croppedData: {
    data: Blob;
    name: string;
    type: string;
  }) {
    uppy.addFile(croppedData);
  }

  async function handleNext() {
    setIsUploadPending(true);
    const filesToUpload = uppy.getFiles();
    if (filesToUpload.length === 0 && uploadedAvatar) {
      uppy.addFile(uploadedAvatar);
    }
    await uppy.upload();
    setIsUploadPending(false);
    handleGoToStep('invitation');
  }

  function handleSkipStep() {
    handleGoToStep('invitation');
  }

  function handleCloseModal() {
    removeQueryParamByKey('action');
    handleReset();
  }

  return (
    <CroeCropper
      uploadedAvatar={uploadedAvatar}
      selectedIcon={selectedIcon}
      Header={
        <Header
          onClose={handleCloseModal}
          Icon={
            selectedIcon ? (
              <StyledImg
                src={selectedIcon}
                alt="avatar preview"
                width={AVATAR_SIZE}
                height={AVATAR_SIZE}
              />
            ) : (
              <ImagePreview />
            )
          }
          title={t(
            'page.workspace.createWorkspaceModal.step2.title',
            'Customize your Knowledge Base',
          )}
          subtitle={t(
            'page.workspace.createWorkspaceModal.step2.description',
            'Make your Knowledge Base instantly recognizable with a unique icon - use your logo, branding elements, a photo, or let Knowz create one for you.',
          )}
        />
      }
      Footer={
        <Footer
          SkipButton={
            <ButtonBase
              disableRipple
              disableTouchRipple
              onClick={handleSkipStep}
            >
              {t('page.workspace.createWorkspaceModal.skipButton', 'Skip Step')}
            </ButtonBase>
          }
          BackButton={
            <Stack
              direction="row"
              gap={3}
            >
              <Button
                size="large"
                onClick={handleBackRedirectToPreviousStep}
                variant="outlined"
                color="secondary"
              >
                {t('page.workspace.createWorkspaceModal.backButton', 'Back')}
              </Button>
            </Stack>
          }
          NextButton={
            <LoadingButton
              onClick={handleNext}
              disabled={isUploadPending}
              loading={isUploadPending}
              size="large"
              variant="contained"
            >
              {t('page.workspace.createWorkspaceModal.nextButton', 'Next')}
            </LoadingButton>
          }
        />
      }
      handleGetCroppedImage={handleGetCroppedImage}
    />
  );
}
